import { useEffect, useState, useMemo, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { PlusIcon } from "@kohler-dsm-library/kohler-icons"
import dynamic from "next/dynamic"
import _isEmpty from "lodash/isEmpty"
import {
  getShortenedUrl,
  getUserPersona,
  getPDPUrlWithConfig,
  getPageType,
} from "@/utils/helper"
import { compareState } from "@/store/features/compareSlice"
import {
  getMyAccountListsWithLineItems,
  selectFavoritesState,
} from "@/store/features/favoritesSlice"
import { selectGenericState } from "@/store/features/genericSlice"
import { useProductContext } from "@/components/ProductList/v3/ProductListContext"
import SaveToList from "@/components/SaveToList"
import ListConfirmationModal from "@/components/ListConfirmationModal"
import { getPresetUrl } from "@/utils/index"
import useWindowResize from "@/hooks/useWindowResize"
import MissingImage from "@/public/images/image-missing.png"
import { useTranslation } from "next-i18next"
import { plpPropsType } from "@/propTypes/plp"
import {
  colorDataLayerHandle,
  getProductInfo,
  removeProductFromListDataLayerPush,
  getEventInfo,
} from "@/components/ProductList/v3/analytics/analytics"
import useProductComparison from "@/hooks/useProductComparison"
import useProductFavorites from "@/hooks/useProductFavorites"
import {
  calculateProductPricing,
  formatPrice,
} from "@/responseProcessors/plpResponseProcessor"
import {
  INDEXEDDB_NOT_SUPPORTED_ERROR,
  CLOSE,
  QUICK_VIEW,
  QUICK_VIEW_MODAL,
} from "@/constants"
import Modal from "@/components/core/Modal/Modal"
import { selectProductListState } from "@/store/features/productListSlice"
import * as indexedDBUtil from "@/utils/indexDB"

const QuickView = dynamic(() => import("@/components/Shared/QuickView"))

const productCardHOC = WrappedComponent => {
  return function WithProductCardWrapper({
    propsProductCard,
    handleOnClickPartsDiagramLink,
    index,
  }) {
    const [width] = useWindowResize()
    const persona = getUserPersona()
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const saveButtonRef = useRef(null)
    const [loginFavouriteCallback, setLoginFavouriteCallback] = useState(null)
    const [showAddToList, setShowAddToList] = useState(false)
    const [deleteFromList, setDeleteFromList] = useState(false)
    const [productSavedToList, setProductSavedToList] = useState(false)
    const [openModal, setOpenModal] = useState(false)
    const [toasterContent, setToasterContent] = useState({
      successMessage: "",
      unfavoriteSuccessMessage: "",
    })
    const [hoverColor, setHoverColor] = useState(null)
    const { comparedProducts, category: productCategory } =
      useSelector(compareState)
    const { isViewDetailClicked } = useSelector(selectProductListState)
    const { toaster } = useSelector(selectGenericState)
    const cartData = useSelector(state => state.cart.data)
    const [initialLoad, setInitialLoad] = useState(true)
    const [scrollY, setScrollY] = useState(0)
    const [compareErrorMessage, setCompareErrorMessage] = useState("") // for compare
    const [isChecked, setIsChecked] = useState(
      comparedProducts.some(
        prod => prod.compareSku === propsProductCard.product.sku
      )
    )
    const [updatedProductCard, setUpdatedProductCard] =
      useState(propsProductCard)
    const [displayPriceState, setDisplayPrice] = useState("")
    const [slashedPriceState, setSlashedPrice] = useState("")
    const [showQuickViewModal, setShowQuickViewModal] = useState(false)
    const skuCode = propsProductCard?.product?.sku
    const productDataId = useMemo(
      () => propsProductCard?.originaldata["id"]?.split("|")[0],
      [propsProductCard]
    )
    const masterSKU = useMemo(
      () => propsProductCard?.originaldata["masterSKU_s"],
      [propsProductCard]
    )

    const {
      general,
      plpServletPath,
      featureFlags,
      isAuthenticated,
      plpLocalState,
      locale,
      skuPrepend,
      componentProps,
      currencySign,
      plpLocalState: { cardsData = [] } = {},
      isServerData = true,
      quickViewAuthData,
      setPlpLocalState,
    } = useProductContext()

    const presetConf = JSON.parse(componentProps.presetConfigs)

    const {
      product: {
        bundleFlag = "no",
        isBundleProduct = false,
        sku: psku = "",
        allVariantsDiscontinued = false,
      } = {},
    } = propsProductCard || {}
    const isBundle = bundleFlag === "Yes"
    const isCanadaSite = general?.siteName === "kohler-canada"
    let retailerExclusive = false
    if (propsProductCard?.originaldata["ProductIsRetail_s"] === "true") {
      retailerExclusive = true
    }
    const isQuickViewShow =
      isBundle ||
      psku?.includes("BNDL") ||
      allVariantsDiscontinued ||
      retailerExclusive

    // SKU logic - use variantList.sku_ss[0] for bundles, masterSKU for regular products
    const effectiveSKU = useMemo(() => {
      try {
        if (
          isBundle &&
          Array.isArray(
            propsProductCard?.originaldata?.["variantList.sku_ss"]
          ) &&
          propsProductCard?.originaldata["variantList.sku_ss"]?.[0]
        ) {
          return propsProductCard.originaldata["variantList.sku_ss"][0]
        }
        if (propsProductCard?.originaldata?.["sku_s"]) {
          return propsProductCard.originaldata["sku_s"]
        }
        console.warn("SKU data is missing. Returning default SKU.")
        return "UNKNOWN_SKU" // Default value
      } catch (error) {
        console.error("Error computing effectiveSKU:", error)
        return "ERROR_SKU" // Fallback value in case of unexpected errors
      }
    }, [isBundle, propsProductCard?.originaldata, masterSKU])

    const { handleCompare, updateCompare } = useProductComparison()
    const { getFavIconStatus, handleFavorite, handleLogin } =
      useProductFavorites()
    const { userListItemsSkus = {} } = useSelector(
      selectFavoritesState,
      (prev, next) => JSON.stringify(prev) === JSON.stringify(next)
    )

    const favorites = useSelector(
      state => state.favorites.favorites?.lineItems || [],
      (prev, next) => JSON.stringify(prev) === JSON.stringify(next)
    )
    const {
      MYSQLCONNSTR_ENABLE_SHOPPING_LISTS_V2: isSaveProductToList = false,
    } = featureFlags
    const favoritesRef = useRef(favorites)
    const userListItemsSkusRef = useRef(userListItemsSkus)

    // Handle focus on save button to ensure it's visible past navigation menu
    const handleSaveButtonFocus = () => {
      if (!saveButtonRef.current) return

      // Determine row cols based on width (same logic as ProductList)
      // Get the element's position
      const rect = saveButtonRef.current.getBoundingClientRect()
      const navHeight = 100 // Approximate navigation menu height, adjust as needed

      // Check if element is hidden behind navigation
      if (rect.top < navHeight) {
        // Scroll to make the element visible with some offset
        const scrollOffset = window.pageYOffset + rect.top - navHeight - 20
        window.scrollTo({
          top: scrollOffset,
          behavior: "smooth",
        })
      }
    }

    const handleQuickViewCTAClick = async (e, sku) => {
      const targetSku = sku ? sku : propsProductCard?.product?.sku
      // Check if IndexedDB is supported, throw error if not
      if (!indexedDBUtil.isSupported()) {
        const error = new Error(INDEXEDDB_NOT_SUPPORTED_ERROR)
        console.error(error)
        throw error
      }
      try {
        // Store data in IndexedDB
        await indexedDBUtil.setItem(
          `plpCardsData+${window?.location?.href}`,
          JSON.stringify(cardsData)
        )
        await indexedDBUtil.setItem("fromPDP", true)
        await indexedDBUtil.setItem("plpsku", targetSku)
        await indexedDBUtil.setItem("isServerData", isServerData)
      } catch (error) {
        console.error("Failed to store data:", error)
      }

      const scrollY = window?.scrollY
      setScrollY(scrollY)
      setShowQuickViewModal(true)

      // add data layer code here
      colorDataLayerHandle(
        propsProductCard,
        index,
        cartData,
        locale,
        skuPrepend,
        general,
        QUICK_VIEW
      )
    }

    const handleQuickViewHide = async () => {
      if (scrollY) {
        window.scrollTo({ top: parseInt(scrollY, 10), behavior: "instant" })
      }
      setScrollY(0)
      setShowQuickViewModal(false)

      // Clean up IndexedDB
      try {
        await indexedDBUtil.removeItem("fromPDP")
        await indexedDBUtil.removeItem(`plpCardsData+${window?.location?.href}`)
        await indexedDBUtil.removeItem("plpsku")
        await indexedDBUtil.removeItem("isServerData")
      } catch (error) {
        console.error("Failed to clean up data:", error)
      }
      // add data layer code here
      colorDataLayerHandle(
        propsProductCard,
        index,
        cartData,
        locale,
        skuPrepend,
        general,
        `${QUICK_VIEW_MODAL}:${CLOSE}`
      )
    }

    useEffect(() => {
      let timeoutId
      if (
        isViewDetailClicked !== "VIEW_DETAILS_CLICKED" &&
        !showQuickViewModal
      ) {
        timeoutId = setTimeout(async () => {
          try {
            await indexedDBUtil.removeItem("fromPDP")
            await indexedDBUtil.removeItem(
              `plpCardsData+${window?.location?.href}`
            )
            await indexedDBUtil.removeItem("plpsku")
            await indexedDBUtil.removeItem("isServerData")
          } catch (error) {
            console.error("Failed to clean up data:", error)
          }
        }, 500) // Remove after 500 milliseconds
      }
      return () => {
        if (timeoutId) clearTimeout(timeoutId)
      }
    }, [isViewDetailClicked, showQuickViewModal])

    useEffect(() => {
      const { displayPrice, hasPriceRange, slashedPrice, skuVariantPrice } =
        calculateProductPricing(
          propsProductCard,
          persona,
          general,
          currencySign,
          isServerData
        )

      // Handle bundle pricing - use priceStartingAt with persona-specific pricing
      let finalDisplayPrice = displayPrice
      if (isBundle && persona) {
        const bundlePriceKey = `priceStartingAt.${persona}_d`
        const bundlePrice = propsProductCard?.originaldata?.[bundlePriceKey]
        if (bundlePrice) {
          finalDisplayPrice = `Starting at ${currencySign}${bundlePrice}`
        } else {
          // Fallback to min_price if persona-specific price is not available
          const fallbackPrice = propsProductCard?.originaldata?.["min_price"]
          if (fallbackPrice) {
            finalDisplayPrice = `Starting at ${currencySign}${fallbackPrice}`
          }
        }
      }

      setUpdatedProductCard(prev => ({
        ...prev,
        price: {
          text: finalDisplayPrice,
          component: "a",
          href: `${getPDPUrlWithConfig(
            propsProductCard?.product?.category,
            propsProductCard?.product?.slug,
            plpServletPath,
            general
          )}?skuId=${propsProductCard?.product?.sku}`,
        },
        hasPriceRange: hasPriceRange,
        skuVariantPrice: skuVariantPrice,
      }))
      if (slashedPrice !== null) {
        setUpdatedProductCard(prev => ({
          ...prev,
          slashedPrice: {
            text: !isBundle && slashedPrice,
            component: "a",
            href: `${getPDPUrlWithConfig(
              propsProductCard?.product?.category,
              propsProductCard?.product?.slug,
              plpServletPath,
              general
            )}?skuId=${propsProductCard?.product?.sku}`,
          },
        }))
      }
      setDisplayPrice(finalDisplayPrice)
      setSlashedPrice(slashedPrice)
    }, [isAuthenticated, width, propsProductCard, isBundle])

    useEffect(() => {
      let timeoutId
      if (compareErrorMessage) {
        timeoutId = setTimeout(() => {
          setCompareErrorMessage("")
        }, 2000)
      }

      if (compareErrorMessage === "" && !initialLoad) {
        const updatedCard = {
          ...propsProductCard,
          ...updatedProductCard,
          // Hide heart icon (save/favorite functionality) for bundles
          ...(!isBundle && {
            isSaved: getFavIconStatus(
              isSaveProductToList,
              productSavedToList,
              userListItemsSkusRef,
              favoritesRef,
              effectiveSKU,
              productDataId,
              null
            ),
            saveCTA: {
              ref: saveButtonRef,
              "aria-label": `Add ${propsProductCard?.product?.productName} to list`,
              onFocus: handleSaveButtonFocus,
              onClick: e => {
                if (isAuthenticated) {
                  handleFavorite(e, {
                    favoritesRef,
                    userListItemsSkusRef,
                    productDataId,
                    masterSKU: effectiveSKU,
                    setOpenModal,
                    setDeleteFromList,
                    setShowAddToList,
                    setLoginFavouriteCallback,
                    toasterContent,
                    propsProductCard,
                    cartData,
                    isChecked,
                    index,
                    locale,
                    skuPrepend,
                    general,
                    isSaveProductToList,
                    isAuthenticated,
                  })
                } else {
                  handleLogin(
                    e,
                    cartData,
                    propsProductCard,
                    setLoginFavouriteCallback,
                    locale,
                    skuPrepend,
                    general
                  )
                }
              },
            },
          }),
          productDetails: {
            ...propsProductCard?.productDetails,
            colorSwatches,
            // Add Bundle tag for bundle products
            tags: propsProductCard?.productDetails?.tags || [],
            // Hide compare checkbox for bundles and only show on desktop
            ...(width > 1023 &&
              !isBundle &&
              !isBundleProduct && {
                checkbox: {
                  ...propsProductCard?.productDetails?.checkbox,
                  "aria-label": `Add ${propsProductCard?.product?.productName}  to compare rack.`,
                  label: t("kf.compare.label.compare"),
                  onClick: e => {
                    handleCompare(
                      e.target.checked,
                      setIsChecked,
                      setInitialLoad,
                      propsProductCard,
                      index,
                      locale,
                      skuPrepend,
                      general,
                      updatedProductCard
                    ) // pass product data and modied responseprocesser
                  },
                },
              }),
          },
        }
        setUpdatedProductCard(updatedCard)
      }
      return () => {
        if (timeoutId) clearTimeout(timeoutId)
      }
    }, [compareErrorMessage, initialLoad])

    useEffect(() => {
      // Update checkbox state when comparedProducts changes
      const isProductCompared = comparedProducts.some(
        prod => prod?.compareSku === propsProductCard?.product?.sku
      )
      if (isChecked !== isProductCompared) {
        setIsChecked(isProductCompared)
      }
    }, [comparedProducts, propsProductCard?.product?.sku])
    // Transform color swatches to include event handlers properly
    // Hide color swatches for bundles where Bundle_s === "Yes"
    const colorSwatches = useMemo(() => {
      if (isBundle) {
        return null // Don't show color swatches for bundles
      }
      return {
        ...propsProductCard?.productDetails?.colorSwatches,
        colors: propsProductCard?.productDetails?.colorSwatches?.colors?.map(
          (item, index) => {
            let slug = propsProductCard?.product?.slug

            const colorSkuSlugListDetails =
              propsProductCard?.originaldata?.[`colorSkuSlugListDetails_ss`]
            if (
              propsProductCard?.isCollapsePLP &&
              !isBundle &&
              !propsProductCard?.product?.isBundleProduct &&
              colorSkuSlugListDetails &&
              colorSkuSlugListDetails.length > 0
            ) {
              const slugFound = colorSkuSlugListDetails.find(
                img => item.sku === img?.split("|")[0]
              )
              if (slugFound) {
                const slugArray = slugFound?.split("|")
                slug =
                  slugArray[slugArray.length - 1] ||
                  propsProductCard?.product?.slug
              }
            }
            const finalUrl = `${getPDPUrlWithConfig(
              propsProductCard?.product?.category,
              slug,
              plpServletPath,
              general
            )}?skuId=${item?.sku}`

            return {
              ...item,
              component: "a",
              href: finalUrl,
              onMouseEnter: () => {
                setHoverColor({
                  sku: item?.sku,
                  customerFacingSKU: item?.customerFacingSKU,
                  image:
                    propsProductCard?.productDetails?.colorSwatches?.colors[
                      index
                    ]?.image,
                  displaySKU: "K-" + item?.sku,
                })
              },
              onMouseLeave: () => {
                setHoverColor(null)
              },
            }
          }
        ),
        onColorSelected: e => {
          const matchFound = propsProductCard?.product?.colors?.find(
            item => typeof item === "string" && item?.split("|")[0] === e
          )
          if (matchFound) {
            redirectToPdpHandler(e, matchFound?.split("|")[2], "color")
          }
        },
      }
    }, [propsProductCard?.productDetails?.colorSwatches, isBundle])

    const redirectToPdpHandler = async (e, sku, type = "") => {
      setPlpLocalState(prev => ({ ...prev, isLoading: true }))
      colorDataLayerHandle(
        propsProductCard,
        index,
        cartData,
        locale,
        skuPrepend,
        general,
        type
      )
      const targetSku = sku || propsProductCard?.product?.sku

      try {
        // Store data in IndexedDB
        await indexedDBUtil.setItem(
          `plpCardsData+${window?.location?.href}`,
          JSON.stringify(cardsData)
        )
        await indexedDBUtil.setItem("fromPDP", true)
        await indexedDBUtil.setItem("plpsku", targetSku)
        await indexedDBUtil.setItem("isServerData", isServerData)
      } catch (error) {
        console.error("Failed to store data:", error)
      }
    }
    // Merge hover state and update image based on selected color
    useMemo(() => {
      const eventInfoServiceParts = getEventInfo(
        t("kf.search.partsdiagram").toLowerCase(),
        null,
        true,
        false,
        false,
        "",
        false,
        propsProductCard,
        index
      )

      const productInfo = getProductInfo(
        "",
        true,
        false,
        cartData,
        propsProductCard,
        locale,
        skuPrepend,
        general
      )

      let swatchImageId = ""
      const colorSkuSlugListDetails =
        propsProductCard?.originaldata?.[`colorSkuSlugListDetails_ss`]
      let swatchImages =
        propsProductCard?.isCollapsePLP &&
        !isBundle &&
        !isBundleProduct &&
        colorSkuSlugListDetails &&
        colorSkuSlugListDetails.length > 0
          ? colorSkuSlugListDetails
          : propsProductCard?.variantImages

      swatchImages = swatchImages?.find(
        img => propsProductCard?.product?.sku === img?.split("|")[0]
      )
      let imgUrl = ""
      let defaultImageName = ""
      // Check if Bundle_s parameter is "true" and use BundlePLPImageURL
      if (
        propsProductCard?.originaldata?.["Bundle_s"] === "Yes" &&
        propsProductCard?.originaldata["BundlePLPImageURL_s"]
      ) {
        imgUrl = propsProductCard?.originaldata["BundlePLPImageURL_s"]
      } else if (swatchImages) {
        swatchImages = swatchImages?.split("|")
        const swatchIndex =
          propsProductCard?.isCollapsePLP &&
          !isBundle &&
          !isBundleProduct &&
          colorSkuSlugListDetails &&
          colorSkuSlugListDetails.length > 0
            ? swatchImages.length - 2
            : swatchImages.length - 1
        const swatchImageValues = swatchImages[swatchIndex]?.split("/")
        swatchImageId =
          swatchImageValues && swatchImageValues.length > 0
            ? swatchImageValues[swatchImageValues.length - 1]
            : ""

        imgUrl = getPresetUrl(
          width,
          width < 1024 ? 2 : 3,
          general.swatchUrl,
          swatchImageId,
          presetConf
        )
        defaultImageName = swatchImageId
      }
      let updatedCard = {
        ...propsProductCard,
        ...updatedProductCard,
        defaultImageName: defaultImageName,
        href: `${getPDPUrlWithConfig(
          propsProductCard?.product?.category,
          propsProductCard?.product?.slug,
          plpServletPath,
          general
        )}?skuId=${propsProductCard?.product?.sku}`,
        image: {
          ...propsProductCard?.image,
          src: imgUrl,
          onError: e => {
            e.currentTarget.onerror = null
            e.currentTarget.src = MissingImage.src
          },
          onMouseEnter: e => {
            handleLightOnPLPProduct(e, imgUrl)
          },
          onMouseLeave: e => {
            handleLightOffPLPProduct(e, imgUrl)
          },
          onClick: redirectToPdpHandler,
        },
        title: {
          ...propsProductCard?.title,
          onClick: redirectToPdpHandler,
          component: "a",
          href: `${getPDPUrlWithConfig(
            propsProductCard?.product?.category,
            propsProductCard?.product?.slug,
            plpServletPath,
            general
          )}?skuId=${propsProductCard?.product?.sku}`,
        },
        description: {
          ...propsProductCard?.description,
          // Use Collapse_Description_s for bundle products
          ...(isBundle &&
            propsProductCard?.originaldata?.["Collapse_Description_s"] && {
              text: propsProductCard?.originaldata["Collapse_Description_s"],
            }),
          onClick: e => {
            redirectToPdpHandler(e)
          },
          component: "a",
          href: `${getPDPUrlWithConfig(
            propsProductCard?.product?.category,
            propsProductCard?.product?.slug,
            plpServletPath,
            general
          )}?skuId=${propsProductCard?.product?.sku}`,
        },
        price: {
          ...updatedProductCard.price,
          onClick: e => {
            redirectToPdpHandler(e)
          },
          component: "a",
          href: `${getPDPUrlWithConfig(
            propsProductCard?.product?.category,
            propsProductCard?.product?.slug,
            plpServletPath,
            general
          )}?skuId=${propsProductCard?.product?.sku}`,
        },
        // Hide heart icon (save/favorite functionality) for bundles
        ...(!isBundle && {
          isSaved: getFavIconStatus(
            isSaveProductToList,
            productSavedToList,
            userListItemsSkusRef,
            favoritesRef,
            effectiveSKU,
            productDataId,
            null
          ),
          saveCTA: {
            ref: saveButtonRef,
            "aria-label": `Add ${propsProductCard?.product?.productName} to list`,
            onFocus: handleSaveButtonFocus,
            onClick: e => {
              if (isAuthenticated) {
                handleFavorite(e, {
                  favoritesRef,
                  userListItemsSkusRef,
                  productDataId,
                  masterSKU: effectiveSKU,
                  setOpenModal,
                  setDeleteFromList,
                  setShowAddToList,
                  setLoginFavouriteCallback,
                  toasterContent,
                  propsProductCard,
                  cartData,
                  isChecked,
                  index,
                  locale,
                  skuPrepend,
                  general,
                  isSaveProductToList,
                  isAuthenticated,
                })
              } else {
                handleLogin(
                  e,
                  cartData,
                  propsProductCard,
                  setLoginFavouriteCallback,
                  locale,
                  skuPrepend,
                  general
                )
              }
            },
          },
        }),
        productDetails: {
          ...propsProductCard?.productDetails,
          SKU: {
            ...propsProductCard?.productDetails?.SKU,
            onClick: e => {
              redirectToPdpHandler(e)
            },
            component: "a",
            href: `${getPDPUrlWithConfig(
              propsProductCard?.product?.category,
              propsProductCard?.product?.slug,
              plpServletPath,
              general
            )}?skuId=${propsProductCard?.product?.sku}`,
          },
          colorSwatches,
          // Add Bundle tag for bundle products
          tags: propsProductCard?.productDetails?.tags || [],
          // Hide compare checkbox for bundles and only show on desktop
          ...(width > 1023 &&
            !isBundle &&
            !isBundleProduct && {
              checkbox: {
                ...propsProductCard?.productDetails?.checkbox,
                label: t("kf.compare.label.compare"),
                "aria-label": `Add ${propsProductCard?.product?.productName}  to compare rack.`,
                checked: isChecked,
                defaultChecked: undefined,
                onClick: e => {
                  handleCompare(
                    e.target.checked,
                    setIsChecked,
                    setInitialLoad,
                    propsProductCard,
                    index,
                    locale,
                    skuPrepend,
                    general
                  )
                },
              },
            }),
          // Hide service parts for bundles
          ...(!isBundle && {
            additionalLink: {
              text:
                propsProductCard?.product?.resourceName &&
                propsProductCard?.product?.productNo
                  ? t("kf.search.serviceparts")
                  : "",
              href: "#",
              "aria-label": `See Service Parts for ${propsProductCard?.product?.productName}`,
              onClick: () => {
                handleOnClickPartsDiagramLink(propsProductCard?.product)
                // updating page content type for service parts click
                const pageEvent = window.pageObj || {}
                const pageType = getPageType()

                const updatedPageEvent = {
                  ...pageEvent,
                  pageInfo: {
                    ...(pageEvent.pageInfo || {}),
                    contentType: pageType,
                  },
                }

                window.adobeDataLayer.push({
                  event: "cmp:click",
                  page: updatedPageEvent,
                  eventInfo: eventInfoServiceParts,
                  productInfo: productInfo,
                })
              },
            },
          }),
        },
      }

      if (hoverColor?.sku) {
        // update sku based on hover color
        const selectedSku = updatedCard?.variantColors?.find(
          color =>
            color?.split("|")[5] === hoverColor?.customerFacingSKU ||
            color?.split("|")[5] === hoverColor?.displaySKU
        )

        const selectedSKUPrice = updatedCard?.skuVariantPrice?.find(
          elem =>
            elem?.split("|")[0] === hoverColor?.customerFacingSKU ||
            elem?.split("|")[0] === hoverColor?.sku
        )
        // If a selected SKU is found, update the productDetails.SKU
        if (selectedSku) {
          updatedCard.productDetails.SKU = {
            ...updatedCard?.productDetails?.SKU,
            text: selectedSku?.split("|")[5],
          }
        }
        // Update image based on selected color
        let swatchImageId = ""
        let swatchImages =
          propsProductCard?.isCollapsePLP &&
          !isBundle &&
          !isBundleProduct &&
          propsProductCard?.originaldata?.[`colorSkuSlugListDetails_ss`]
            ?.length > 0
            ? propsProductCard?.originaldata[`colorSkuSlugListDetails_ss`]
            : propsProductCard?.variantImages

        swatchImages = swatchImages?.find(
          img => hoverColor && hoverColor.sku === img?.split("|")[0]
        )
        if (swatchImages) {
          swatchImages = swatchImages?.split("|")
          const swatchIndex =
            propsProductCard?.isCollapsePLP &&
            !isBundle &&
            !isBundleProduct &&
            propsProductCard?.originaldata[`colorSkuSlugListDetails_ss`]
              ?.length > 0
              ? swatchImages.length - 2
              : swatchImages.length - 1

          const swatchImageValues = swatchImages[swatchIndex]?.split("/")
          swatchImageId =
            swatchImageValues && swatchImageValues.length > 0
              ? swatchImageValues[swatchImageValues.length - 1]
              : ""

          const imgUrl = getPresetUrl(
            width,
            width < 1024 ? 2 : 3,
            general.swatchUrl,
            swatchImageId,
            presetConf
          )
          updatedCard.defaultImageName = swatchImageId
          updatedCard.image = {
            ...updatedCard.image,
            src: imgUrl,
            onError: e => {
              e.currentTarget.onerror = null
              e.currentTarget.src = MissingImage.src
            },
            alt: hoverColor?.image?.alt || "",
          }
          updatedCard.price = {
            ...updatedCard?.price,
            ...(!updatedCard?.hasPriceRange && {
              text: formatPrice(selectedSKUPrice?.split("|")[2], currencySign),
              component: "a",
              href: `${getPDPUrlWithConfig(
                propsProductCard?.product?.category,
                propsProductCard?.product?.slug,
                plpServletPath,
                general
              )}?skuId=${selectedSKUPrice?.split("|")[0]}`,
            }),
          }
        }
      }
      // on mouse leave display old price
      if (typeof hoverColor?.sku === "undefined") {
        updatedCard = {
          ...updatedCard,
          price: {
            text: displayPriceState,
            component: "a",
            href: `${getPDPUrlWithConfig(
              propsProductCard?.product?.category,
              propsProductCard?.product?.slug,
              plpServletPath,
              general
            )}?skuId=${propsProductCard?.product?.sku}`,
          },
        }
        if (slashedPriceState !== "" && slashedPriceState !== null) {
          updatedCard = {
            ...updatedCard,
            slashedPrice: {
              text: !isBundle && slashedPriceState,
              component: "a",
              href: `${getPDPUrlWithConfig(
                propsProductCard?.product?.category,
                propsProductCard?.product?.slug,
                plpServletPath,
                general
              )}?skuId=${propsProductCard?.product?.sku}`,
            },
          }
        }
      }

      if (!isQuickViewShow && !isCanadaSite) {
        updatedCard.quickViewCTA = {
          label: t("kf.plp.product.quick.view.label"),
          "aria-label": `Open Quick View for  ${propsProductCard?.product?.productName}`,
          icon: PlusIcon,
          onClick: e => {
            handleQuickViewCTAClick(e, propsProductCard?.product?.sku)
          },
        }
      }
      setUpdatedProductCard({
        ...updatedProductCard,
        ...updatedCard,
      })
    }, [propsProductCard, hoverColor?.sku, width, isAuthenticated])

    useEffect(() => {
      if (!initialLoad) {
        updateCompare(isChecked, {
          propsProductCard,
          comparedProducts,
          productCategory,
          setCompareErrorMessage,
          setIsChecked,
          productSavedToList,
          userListItemsSkusRef,
          favoritesRef,
          masterSKU: effectiveSKU,
          productDataId,
          colorSwatches,
          compareErrorMessage,
          setUpdatedProductCard,
          setInitialLoad,
          index,
          setLoginFavouriteCallback,
          setOpenModal,
          setDeleteFromList,
          setShowAddToList,
          toasterContent,
          general,
          presetConf,
          plpServletPath,
          plpLocalState,
          isSaveProductToList,
          isAuthenticated,
          locale,
          skuPrepend,
          updatedProductCard,
        })
      }
    }, [isChecked, initialLoad])

    useEffect(() => {
      favoritesRef.current = favorites
      const isCurrentlyFavorite = favorites?.some(
        fav => fav.productId === productDataId
      )
      const newIsSaved = getFavIconStatus(
        isSaveProductToList,
        productSavedToList,
        userListItemsSkusRef,
        favoritesRef,
        effectiveSKU,
        productDataId,
        isCurrentlyFavorite
      )

      setUpdatedProductCard(prev => {
        if (prev.isSaved !== newIsSaved) {
          return { ...prev, isSaved: newIsSaved }
        }
        return prev
      })
    }, [favorites.length])

    useEffect(() => {
      userListItemsSkusRef.current = userListItemsSkus
      const isCurrentlyFavorite = !!userListItemsSkus[effectiveSKU]
      const newIsSaved = getFavIconStatus(
        isSaveProductToList,
        productSavedToList,
        userListItemsSkusRef,
        favoritesRef,
        effectiveSKU,
        productDataId,
        isCurrentlyFavorite
      )
      setUpdatedProductCard(prev => {
        if (prev.isSaved !== newIsSaved) {
          return { ...prev, isSaved: newIsSaved }
        }
        return prev
      })
    }, [userListItemsSkus, effectiveSKU])

    const setToasterMessage = async () => {
      if (!_isEmpty(toaster?.content)) {
        const { successMessage, unfavoriteSuccessMessage } = toaster.content
        setToasterContent({
          successMessage: await getShortenedUrl(successMessage),
          unfavoriteSuccessMessage: await getShortenedUrl(
            unfavoriteSuccessMessage
          ),
        })
      }
    }

    const handleLightOnPLPProduct = e => {
      if (componentProps?.hoverImageTag) {
        const imagesVarints =
          propsProductCard?.product?.productVariantImages ?? []

        const hoverImage = imagesVarints.find(imageString => {
          const imageDetails = imageString.split("|")

          return (
            imageDetails[2] === componentProps.hoverImageTag &&
            imageDetails[0] === propsProductCard?.product?.sku
          )
        })

        if (hoverImage) {
          const hoverImageDetails = hoverImage.split("|")
          const imgUrlNew = getPresetUrl(
            width,
            width < 1024 ? 2 : 3,
            general.swatchUrl,
            hoverImageDetails[1],
            presetConf
          )
          e.currentTarget.src = imgUrlNew
        }
      }
    }
    const handleLightOffPLPProduct = (e, imgUrl) => {
      if (componentProps?.hoverImageTag) {
        e.currentTarget.src = imgUrl
      }
    }

    useEffect(() => {
      setToasterMessage()
    }, [])

    useEffect(() => {
      if (loginFavouriteCallback && isAuthenticated) {
        if (isSaveProductToList) {
          dispatch(getMyAccountListsWithLineItems())
            .unwrap()
            .then(res => {
              const userlistSKUS = res[0][1]
              if (userlistSKUS[effectiveSKU]) {
                setOpenModal(true)
                setDeleteFromList(true)
              } else {
                handleFavorite(loginFavouriteCallback, {
                  favoritesRef,
                  userListItemsSkusRef,
                  productDataId,
                  masterSKU: effectiveSKU,
                  setOpenModal,
                  setDeleteFromList,
                  setShowAddToList,
                  setLoginFavouriteCallback,
                  toasterContent,
                  propsProductCard,
                  cartData,
                  isChecked,
                  index,
                  locale,
                  skuPrepend,
                  general,
                  isSaveProductToList,
                  isAuthenticated,
                  skipDataLayer: true,
                })
              }
            })
        } else {
          handleFavorite(loginFavouriteCallback, {
            favoritesRef,
            userListItemsSkusRef,
            productDataId,
            masterSKU: effectiveSKU,
            setOpenModal,
            setDeleteFromList,
            setShowAddToList,
            setLoginFavouriteCallback,
            toasterContent,
            propsProductCard,
            cartData,
            isChecked,
            index,
            locale,
            skuPrepend,
            general,
            isSaveProductToList,
            isAuthenticated,
          })
        }
      }
    }, [loginFavouriteCallback, isAuthenticated])

    const deleteFromPLPDataLayer = () => {
      const productInfo = getProductInfo(
        "",
        true,
        false,
        cartData,
        propsProductCard,
        locale,
        skuPrepend,
        general
      )
      removeProductFromListDataLayerPush("delete from list", productInfo)
    }

    const handleDeleteFromList = () => {
      setProductSavedToList(false)
      const productInfo = getProductInfo(
        "",
        true,
        false,
        cartData,
        propsProductCard,
        locale,
        skuPrepend,
        general
      )
      removeProductFromListDataLayerPush("delete from list", productInfo)
    }

    const handleSaveToListAnalyticsInfo = () => {
      const productInfo = getProductInfo(
        "",
        true,
        false,
        cartData,
        propsProductCard,
        locale,
        skuPrepend,
        general
      )
      removeProductFromListDataLayerPush("save to List", productInfo)
    }

    const handleSaveToListSuccessCallBack = () => {
      setProductSavedToList(true)
    }

    let modalComponent = null
    if (openModal) {
      modalComponent = (
        <ListConfirmationModal
          openConforMationModal={openModal}
          deleteFromList={deleteFromList}
          deleteFromPLP={true}
          deleteFromPLPDataLayer={deleteFromPLPDataLayer}
          closeConformationModal={() => {
            setOpenModal(false)
          }}
          list={userListItemsSkus[skuCode]}
          deleteConfirmationModal={() => {
            handleDeleteFromList()
          }}
        />
      )
    } else if (showAddToList) {
      modalComponent = (
        <SaveToList
          productSku={skuCode}
          showListSaveAndCreate={showAddToList}
          listSaveAndCreateClose={() => {
            setShowAddToList(false)
          }}
          fromPLP={true}
          plpAddToListAnalytics={() => {
            handleSaveToListAnalyticsInfo()
          }}
          handleSuccessCallBack={() => handleSaveToListSuccessCallBack()}
        />
      )
    }

    return (
      <div className="product-list-tile__wrapper wow">
        <WrappedComponent {...updatedProductCard} />
        {modalComponent}
        {showQuickViewModal ? (
          <Modal
            showModal={showQuickViewModal}
            onModalClose={handleQuickViewHide}
            className="quickview-modal"
            fromPLP={true}
          >
            <QuickView
              productData={propsProductCard?.originaldata}
              closeModal={handleQuickViewHide}
              authorData={quickViewAuthData}
              quickViewSelectedSku={skuCode}
              plp={true}
            />
          </Modal>
        ) : null}
      </div>
    )
  }
}

// PropTypes for the wrapped component
productCardHOC.propTypes = plpPropsType

export default productCardHOC
